/*!
 * Omneo Lists — UI Module
 * -------------------------------------------------------------------------
 * Binds product-page and quickview "Add to Wishlist" / "Add to Reserve
 * List" buttons to the core module's public API (window.OmneoLists).
 *
 * This replaces two separate, drifting copies of the same click-handler
 * logic that previously lived in theme.js: one for the static product
 * page (around the old initListButtons()) and one for the quickview
 * modal. Using event delegation on `document` means ONE handler covers
 * both — including quickview markup that's injected after page load.
 *
 * SCOPING — confirmed via staging testing (2026-07-01): when quickview
 * is open on top of an underlying product page, TWO copies of
 * .formVariantId (and .product_form, .AddtoWishlist, swatches, etc.)
 * exist in the DOM simultaneously — one from the page, one injected by
 * quickview. Every DOM lookup in this file is scoped via
 * getActiveRoot() to whichever one is actually being interacted with,
 * rather than querying the whole document and risking grabbing the
 * wrong instance's data.
 *
 * Requires: window.jQuery, window.OmneoLists (omneo-lists.js, loaded first)
 * -------------------------------------------------------------------------
 */
(function ($, OmneoLists) {
  'use strict';

  if (!$) {
    console.warn('[OmneoLists UI] jQuery not found — button binding skipped.');
    return;
  }
  if (!OmneoLists) {
    console.warn('[OmneoLists UI] Core module (omneo-lists.js) not loaded — button binding skipped.');
    return;
  }

  var LABELS = {
    wish_list: 'WISH LIST',
    reserve_list: 'RESERVE LIST'
  };

  var SWATCH_CLASS = {
    wish_list: 'in-wishlist',
    reserve_list: 'in-reservelist'
  };

  // ---------------------------------------------------------------------
  // Root scoping
  //
  // Returns the jQuery-wrapped root to scope all subsequent lookups
  // within: the quickview modal if the interaction is happening inside
  // it (or it's currently open, for hydration calls with no specific
  // triggering element), otherwise the whole document.
  //
  // $contextEl, if given, is the element the user actually interacted
  // with (e.g. the clicked button/swatch) — used to detect "is this
  // click happening inside the quickview modal" via $.contains().
  // ---------------------------------------------------------------------
  function getActiveRoot($contextEl) {
    var modal = document.getElementById('modal-quickview-cart');
    if (!modal) return $(document);
    if ($contextEl && $contextEl.length && $.contains(modal, $contextEl[0])) {
      return $(modal);
    }
    if (modal.classList.contains('modal--active')) {
      return $(modal);
    }
    return $(document);
  }

  // NOTE: $root.find('.product_form') rather than .closest() from the
  // button — .AddtoWishlist/.AddtoReserveList aren't nested inside
  // .product_form in this theme's markup (confirmed via testing,
  // .closest('.product_form') returned null even with a variant
  // selected). $root itself (document, or the quickview modal) is what
  // provides the correct scope instead.
  function getContext($root) {
    var $form = $root.find('.product_form');
    return {
      $form: $form,
      handle: $form.data('product-handle'),
      productId: $form.data('product-id'),
      variantId: $root.find('.formVariantId').val(),
      sku: $root.find('.variant_sku').val(),
      // Added for Zapier tracking readability — matches the element
      // the old custom.js Zapier integration used for the same purpose.
      title: $root.find('.product-title').first().text().trim()
    };
  }

  function setButtonAdded($btn, added) {
    $btn.toggleClass('added', added);
    $btn.siblings('.tooltip').toggleClass('added', added);
  }

  // NOTE: variant IDs are pure numbers (e.g. "42640368009312"). Building
  // a CSS selector string like '.' + variantId is invalid — class
  // selectors can't start with a digit unescaped, so
  // '.swatches__swatch--regular.42640368009312' throws a SyntaxError.
  // The legacy code avoided this by looping elements and checking
  // classList.contains() (a plain string comparison, no CSS parsing)
  // rather than querying by a selector built from the ID. Matching that
  // here instead of CSS.escape()-ing, to stay consistent with the rest
  // of the theme's approach.
  function setSwatchState(variantId, type, added, $root) {
    if (!variantId) return;
    $root.find('.swatches__swatch--regular').each(function () {
      if (this.classList.contains(String(variantId))) {
        this.classList.toggle(SWATCH_CLASS[type], added);
      }
    });
  }

  // Messaging elements are referenced by ID (#list-messaging,
  // #list-messaging-login). If quickview's injected markup duplicates
  // those IDs (technically invalid HTML but common in AJAX-injected
  // content), a plain document.getElementById() would always return
  // the first one in the DOM regardless of which context is active.
  // Scoping via $root.find() avoids that — it still works correctly
  // even against duplicate IDs, since it only searches within $root's
  // subtree.
  function showSizeRequiredMessage($root, type) {
    var $el = $root.find('#list-messaging');
    if ($el.length) $el.html('<p>! To add to your <b>' + LABELS[type] + '</b>, please select your Size</p>');
  }

  function showManageOnListPageMessage($root, type) {
    var $el = $root.find('#list-messaging-login');
    if ($el.length) {
      $el.html('<p>To manage your <b>' + LABELS[type] + '</b> products go to ' +
        '<a href="/pages/list">Your List</a> page.</p>');
    }
  }

  function bindListButton(selector, type) {
    $(document).on('click', selector, function () {
      var $btn = $(this);
      var $root = getActiveRoot($btn);

      // Reserve List requires being logged in (Level 3/4/Team Fave,
      // always logged in per business rules) — checked explicitly here
      // rather than trusting the button's visibility alone (CSS
      // level-3-plus/tier-class gating, same class of gap found and
      // fixed elsewhere for guest sessions). Prevents a guest from ever
      // creating a reserve_list cookie entry in the first place, rather
      // than only cleaning up the consequences of one that already
      // exists (see hydrateListType()'s guard for that side).
      if (type === 'reserve_list' && !OmneoLists._getConfig().isLoggedIn) {
        return;
      }

      // Reserve List enforces one size per product (legacy rule,
      // confirmed via omneo-lists.liquid's SKU-prefix matching + "Only
      // one size can be added" message). Checking $btn.hasClass('added')
      // alone isn't enough — that only reflects the CURRENTLY SELECTED
      // swatch. Switching to a different, not-yet-reserved size cleared
      // that class (see the swatch click handler below), which silently
      // let a second size through. Checking whether ANY swatch within
      // this root carries 'in-reservelist' catches the product-level
      // conflict regardless of which size is currently selected. This
      // also means Reserve List is intentionally never toggle-able from
      // the product page once one size is reserved — same message
      // either way, per business decision.
      if (type === 'reserve_list') {
        var anySizeReserved = $btn.hasClass('disabled') || $btn.hasClass('added') ||
          $root.find('.swatches__swatch--regular.in-reservelist').length > 0;
        if (anySizeReserved) {
          showManageOnListPageMessage($root, type);
          return;
        }
      }

      var ctx = getContext($root);
      if (!ctx.variantId) {
        showSizeRequiredMessage($root, type);
        return;
      }

      var wasAdded = $btn.hasClass('added');

      // Optimistic UI update — matches prior behaviour of toggling
      // immediately rather than waiting on the Omneo round-trip.
      setButtonAdded($btn, !wasAdded);
      setSwatchState(ctx.variantId, type, !wasAdded, $root);

      if (wasAdded) {
        var omneoItemId = $btn.data('omneo-item-id');
        OmneoLists.remove(type, ctx.variantId, omneoItemId, { title: ctx.title });
      } else {
        // Capture the newly-created Omneo item's ID once add() resolves
        // and store it on both the button and the swatch — without
        // this, a subsequent remove click (within the same page
        // session, before any reload) would have no ID to tell Omneo
        // which record to delete, silently skipping the Omneo-side
        // delete while the UI showed it as removed. Re-adding after
        // that created a second, orphaned record. Confirmed via a real
        // report of duplicate backend records — fixed here and in
        // add()'s response handling in omneo-lists.js.
        OmneoLists.add(type, ctx.variantId, ctx.sku, {
          handle: ctx.handle,
          productId: ctx.productId,
          title: ctx.title
        }).then(function (result) {
          if (!result || !result.omneoItemId) return;
          // Only apply if the button still reflects this add — guards
          // against a rapid remove happening before this resolves.
          if ($btn.hasClass('added')) {
            $btn.data('omneo-item-id', result.omneoItemId);
          }
          $root.find('.swatches__swatch--regular').each(function () {
            if (this.classList.contains(String(ctx.variantId))) {
              this.setAttribute('data-omneo-item-id-' + type, result.omneoItemId);
            }
          });
        });
      }
    });
  }

  bindListButton('.AddtoWishlist', 'wish_list');
  bindListButton('.AddtoReserveList', 'reserve_list');

  // Restore per-variant button state when a size swatch is selected —
  // one handler for both the product page and quickview swatches.
  //
  // NOTE: removing the `no-default-size` class (which reveals the
  // selected-swatch border via custom.css) is intentionally NOT handled
  // here — that's core product-page behaviour, not list-specific, and
  // now lives in theme.js's Swatch() handler instead. See
  // docs/omneo-lists-README.md §9 for details on this dependency.
  $(document).on('click', '.swatches__swatch--regular', function () {
    var $swatch = $(this);
    var $root = getActiveRoot($swatch);

    var $messaging = $root.find('#list-messaging');
    if ($messaging.length) $messaging.html('');

    var $wishBtn = $root.find('.AddtoWishlist');
    var $reserveBtn = $root.find('.AddtoReserveList');

    $wishBtn.removeClass('added').siblings('.tooltip').removeClass('added');
    $wishBtn.removeData('omneo-item-id');
    if ($swatch.hasClass('in-wishlist')) {
      $wishBtn.addClass('added').siblings('.tooltip').addClass('added');
      var wishOmneoId = $swatch.attr('data-omneo-item-id-wish_list');
      if (wishOmneoId) $wishBtn.data('omneo-item-id', wishOmneoId);
    }

    // NOTE: reserve button state is intentionally product-level, not
    // scoped to the currently selected swatch — same reasoning as the
    // bindListButton() guard above. Checking within $root only, so a
    // reserved size in the underlying page doesn't bleed into a
    // quickview for a different product (or vice versa).
    var anySizeReserved = $root.find('.swatches__swatch--regular.in-reservelist').length > 0;
    $reserveBtn.toggleClass('added', anySizeReserved).siblings('.tooltip').toggleClass('added', anySizeReserved);
    if (anySizeReserved) {
      var $reservedSwatch = $root.find('.swatches__swatch--regular.in-reservelist').first();
      var reserveOmneoId = $reservedSwatch.attr('data-omneo-item-id-reserve_list');
      if (reserveOmneoId) $reserveBtn.data('omneo-item-id', reserveOmneoId);
    } else {
      $reserveBtn.removeData('omneo-item-id');
    }
  });

  // Keep the header wishlist icon in sync with session state without a
  // full page reload. Header is unique/page-level — not duplicated by
  // quickview — so this doesn't need root scoping.
  OmneoLists.on('session:updated', function (detail) {
    if (detail.type !== 'wish_list') return;
    $('.headerItem.wishlist').toggleClass('empty', detail.count === 0);
  });

  // ---------------------------------------------------------------------
  // Cross-device hydration from live Omneo list data
  //
  // Everything below (hydrateProductPage etc.) only reads the local
  // session cookie — a customer logging in on a second device with no
  // cookie sees nothing, even though their Omneo profile has saved
  // items. This section fixes that by reading the actual Omneo list
  // data once auth completes, and ALSO attaches Omneo item IDs to
  // swatches/buttons so remove() can sync deletions back to Omneo
  // (previously impossible — nothing set that ID anywhere).
  //
  // Omneo list items are keyed by SKU, but swatches/buttons on this page
  // are keyed by Shopify variant ID. The legacy system bridged this with
  // one /search?q={sku} AJAX call per saved item (see
  // templates/search.your-list.liquid). Using the existing .product-json
  // blob instead — already rendered on every product page (see
  // main-product.liquid, `{% render 'product-json' %}`) and already
  // parsed by theme.js's own Product() function — avoids that network
  // round-trip entirely, since it already contains every variant's SKU
  // and ID for this product.
  //
  // ⚠ Confirmed against real Omneo list payloads on staging (2026-07-01),
  // both wish-list and reserve-list: dataObject['wish-list'/'reserve-list'].items
  // (object keyed by string item IDs), item.product_variant.sku, item.id
  // all match as assumed. The product-matching field did NOT match as
  // originally assumed — see getCleanProductHandle() below. reserve-list
  // items also carry a `status` field ("Not Taken", "Archived",
  // "Allocated", "Unallocated", or null) not present on wish-list items
  // — see the status filter in applyOmneoListItem() below.
  // ---------------------------------------------------------------------
  var OMNEO_LIST_ID_TO_TYPE = {
    'wish-list': 'wish_list',
    'reserve-list': 'reserve_list'
    // 'recommended-for-you-list' intentionally excluded — not a
    // product-page button, belongs to the not-yet-migrated lists-page
    // rendering work.
  };

  var lastOmneoListsData = null;

  function getProductJson($root) {
    var el = $root.find('.product-json')[0];
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || el.innerHTML || '{}');
    } catch (e) {
      console.warn('[OmneoLists UI] Could not parse .product-json', e);
      return null;
    }
  }

  function getVariantIdForSku(sku, $root) {
    var product = getProductJson($root);
    if (!product || !Array.isArray(product.variants)) return null;
    var match = product.variants.filter(function (v) { return v.sku === sku; })[0];
    return match ? match.id : null;
  }

  // Reserve-list items carry a `status` field that wish-list items don't:
  // "Not Taken", "Archived", "Allocated", "Unallocated", or null.
  //
  // - "Not Taken" / "Archived": resolved/closed — never actively
  //   reserved. Matches the documented business rule.
  // - Any other non-null status (e.g. "Allocated", "Unallocated"):
  //   actively reserved.
  // - null: a customer-initiated reservation Omneo hasn't processed
  //   into a resolved status yet. CORRECTED based on staging testing —
  //   an earlier version of this code treated null as always active,
  //   but confirmed via a real example (Watson Coat, status: null) that
  //   this is wrong for items staff never got to. Null is now only
  //   treated as active within a grace period from creation
  //   (RESERVE_NULL_STATUS_GRACE_DAYS); past that, treated the same as
  //   "Not Taken"/"Archived" — unlocked, Wishlist available again. This
  //   only affects the product-page lock/button behaviour — the actual
  //   Reserve List page (not yet built, Phase 2) would still show the
  //   item regardless of this grace period, per business decision.
  var RESERVE_STATUSES_TO_IGNORE = ['Not Taken', 'Archived'];
  var RESERVE_NULL_STATUS_GRACE_DAYS = 7;

  // Omneo dates look like "2026-04-10 01:02:31" — space-separated, not
  // directly cross-browser parseable by `new Date()` (Safari can choke
  // on this format). Normalized to ISO 8601 here.
  // ⚠ ASSUMPTION: treated as UTC. Not confirmed against Omneo's actual
  // timezone handling — worth verifying if the grace period ever seems
  // off by several hours.
  function parseOmneoDate(dateStr) {
    if (!dateStr) return null;
    var iso = dateStr.replace(' ', 'T') + 'Z';
    var d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
  }

  function isReserveItemActive(item) {
    if (RESERVE_STATUSES_TO_IGNORE.indexOf(item.status) !== -1) return false;
    if (item.status) return true;

    // status is null — active only within the grace period from creation.
    var createdAt = parseOmneoDate(item.created_at);
    if (!createdAt) return true; // can't determine age — fail toward treating as active
    var ageMs = Date.now() - createdAt.getTime();
    var graceMs = RESERVE_NULL_STATUS_GRACE_DAYS * 24 * 60 * 60 * 1000;
    return ageMs <= graceMs;
  }

  function applyOmneoListItem(type, item, $root) {
    if (type === 'reserve_list' && !isReserveItemActive(item)) {
      return;
    }

    var sku = item.product_variant && item.product_variant.sku;
    var variantId = sku ? getVariantIdForSku(sku, $root) : null;
    // No match likely means this saved item belongs to a different
    // product than the one currently on screen — not an error.
    if (!variantId) return;

    $root.find('.swatches__swatch--regular').each(function () {
      if (this.classList.contains(String(variantId))) {
        this.classList.add(SWATCH_CLASS[type]);
        this.setAttribute('data-omneo-item-id-' + type, item.id);
      }
    });

    if (type === 'reserve_list') {
      setButtonAdded($root.find('.AddtoReserveList'), true);
      $root.find('.AddtoReserveList').data('omneo-item-id', item.id);
      $root.find('.AddtoWishlist').hide();
    } else {
      var currentVariantId = $root.find('.formVariantId').val();
      if (String(variantId) === String(currentVariantId)) {
        setButtonAdded($root.find('.AddtoWishlist'), true);
        $root.find('.AddtoWishlist').data('omneo-item-id', item.id);
      }
    }
  }

  // Confirmed against a real Omneo payload (2026-07-01): item.product.external_id
  // is a short internal reference (e.g. "6122") baked into the product
  // handle as a suffix ("olly-dress-spring-crush-citrus-6122") — it is
  // NOT the Shopify numeric product ID (which looks like
  // "7703833739360"). An earlier version of this code compared
  // external_id directly against Shopify's product-id and would have
  // silently matched nothing. The legacy omneo-lists.liquid code only
  // ever used external_id to strip that suffix off the handle — matching
  // by the resulting clean HANDLE against the page's product-handle is
  // correct, matching by external_id-as-product-id is not.
  function getCleanProductHandle(product) {
    if (!product || !product.handle) return null;
    var externalId = product.external_id;
    if (!externalId) return product.handle;
    var suffix = '-' + externalId;
    return product.handle.slice(-suffix.length) === suffix
      ? product.handle.slice(0, -suffix.length)
      : product.handle;
  }

  function hydrateFromOmneoLists(dataObject, $root) {
    lastOmneoListsData = dataObject;
    if (!dataObject) return;
    $root = $root || $(document);

    var currentProductHandle = $root.find('.product_form').data('product-handle');
    if (!currentProductHandle) return;

    Object.keys(OMNEO_LIST_ID_TO_TYPE).forEach(function (omneoListId) {
      var type = OMNEO_LIST_ID_TO_TYPE[omneoListId];
      var list = dataObject[omneoListId];
      if (!list || !list.items) return;

      Object.values(list.items).forEach(function (item) {
        var productHandle = getCleanProductHandle(item.product);
        if (productHandle !== currentProductHandle) return;
        applyOmneoListItem(type, item, $root);
      });
    });
  }

  // Registered with no $root — fires from the core module's auth flow,
  // not from a specific click, so defaults to document scope inside
  // hydrateFromOmneoLists() above. observeQuickview() below calls this
  // again explicitly scoped to the modal when quickview opens.
  OmneoLists.on('lists:ready', function (data) { hydrateFromOmneoLists(data); });
  OmneoLists.on('lists:update', function (data) { hydrateFromOmneoLists(data); });

  // ---------------------------------------------------------------------
  // Initial-state hydration
  //
  // Restores wishlist/reserve-list visual state (swatch highlighting,
  // button "added" state, header icon) from the session cookie when a
  // page loads. Previously handled by Shopify.RecordList.viewedListProducts()
  // inside theme.js's initListButtons(), which is now disabled — without
  // this, a product already in a list showed no indication of that on
  // reload.
  //
  // Deliberately NOT ported: the legacy code also auto-redirected the
  // page to append "?variant=X" for a saved size on load. Confirmed with
  // Juliette Hogan to leave this out — see docs/omneo-lists-README.md §9b.
  //
  // This only reads from the session cookie — no Omneo/auth dependency,
  // matching the legacy behaviour of working for guests too.
  // ---------------------------------------------------------------------
  function hydrateHeaderIcon() {
    var count = OmneoLists.getCount('wish_list');
    $('.headerItem.wishlist').toggleClass('empty', count === 0);
  }

  function hydrateListType(type, productId, $root) {
    // Reserve List session data is only ever meaningful for logged-in
    // customers (Level 3/4/Team Fave, always logged in per business
    // rules) — a guest should never have this treated as valid, however
    // a stray entry got into the cookie (e.g. leftover from earlier
    // logged-in testing in the same browser). Same class of gap as the
    // list-page "Move to reserve list" fix — CSS-only tier gating isn't
    // solid enough on its own; check login state directly here too.
    // Found via guest-session testing: a coming-soon product's Wishlist
    // button was missing for a guest, traced to a leftover reserve_list
    // cookie entry from prior logged-in testing being treated as real.
    if (type === 'reserve_list' && !OmneoLists._getConfig().isLoggedIn) return;

    var entries = OmneoLists.getEntriesForProduct(type, productId);
    if (!entries.length) return;

    entries.forEach(function (entry) {
      $root.find('.swatches__swatch--regular').each(function () {
        if (this.classList.contains(String(entry.variantId))) {
          this.classList.add(SWATCH_CLASS[type]);
        }
      });
    });

    if (type === 'reserve_list') {
      // One-size-per-product rule: ANY match for this product means
      // "already reserved", regardless of which variant is currently
      // selected — matches the product-level check in bindListButton().
      setButtonAdded($root.find('.AddtoReserveList'), true);
      // Legacy behaviour: hide the Wishlist button entirely once a size
      // is reserved for this product. Ported as-is — flag if this isn't
      // actually wanted, it's a business-rule assumption, not a
      // technical necessity.
      $root.find('.AddtoWishlist').hide();
    } else {
      // Wish List: only mark "added" if the CURRENTLY SELECTED variant
      // specifically matches — unlike reserve list, multiple sizes can
      // be wishlisted independently.
      var currentVariantId = $root.find('.formVariantId').val();
      var matchesCurrent = entries.some(function (e) {
        return String(e.variantId) === String(currentVariantId);
      });
      setButtonAdded($root.find('.AddtoWishlist'), matchesCurrent);
    }
  }

  function hydrateProductPage($root) {
    $root = $root || $(document);
    var productId = $root.find('.product_form').data('product-id');
    if (!productId) return;

    hydrateListType('wish_list', productId, $root);
    hydrateListType('reserve_list', productId, $root);
  }

  // Quickview injects its own copy of this markup into a modal after
  // page load, on its own timeline. Watching the modal element's class
  // attribute (only place `modal--active` is ever added — confirmed in
  // theme.js) tells us when to hydrate that modal's content.
  //
  // BUG FIX HISTORY (see docs/omneo-lists-README.md for full details):
  // an earlier version watched the whole modal subtree, which caused
  // hydration's own class mutations to re-trigger this same observer —
  // an infinite loop that froze the page. Narrowed to just the class
  // attribute on the modal element itself, so hydration mutations
  // elsewhere in the subtree are invisible to this observer.
  function observeQuickview() {
    var modal = document.getElementById('modal-quickview-cart');
    if (!modal) return;
    var $modal = $(modal);

    var observer = new MutationObserver(function () {
      if (modal.classList.contains('modal--active')) {
        hydrateProductPage($modal);
        // Re-apply Omneo-sourced state for the quickview's product too —
        // lists:update/lists:ready likely already fired once on page
        // load, before this product's markup existed, so re-run against
        // the cached payload rather than waiting for another event.
        if (lastOmneoListsData) hydrateFromOmneoLists(lastOmneoListsData, $modal);
      }
    });
    observer.observe(modal, { attributes: true, attributeFilter: ['class'] });
  }

  hydrateHeaderIcon();
  if (document.body.className.indexOf('template-product') > -1) {
    hydrateProductPage($(document));
  }
  observeQuickview();

})(window.jQuery, window.OmneoLists);